import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { listCustomers, type Customer } from "../api/portal";

/**
 * Top-bar customer picker for internal users.
 *
 * Switching preserves the sub-page you are on (switching customers while
 * looking at Reports keeps you on Reports), because the selected customer
 * lives in the URL rather than in component state.
 */
export function CustomerSwitcher({ clientId }: { clientId?: string }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  const { data } = useQuery({ queryKey: ["customers"], queryFn: listCustomers });
  const customers: Customer[] = data?.customers ?? [];

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const current = customers.find((c) => c.client_id === clientId);

  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter(
      (c) => c.client_name.toLowerCase().includes(q) || c.client_id.toLowerCase().includes(q),
    );
  }, [customers, filter]);

  // The trailing segment of /customers/:clientId/<sub> -- empty for the
  // dashboard. Carried across a switch so you stay on the same kind of page.
  const subPage = useMemo(() => {
    const m = location.pathname.match(/^\/customers\/[^/]+\/(.*)$/);
    return m ? m[1] : "";
  }, [location.pathname]);

  const select = (id: string) => {
    setOpen(false);
    setFilter("");
    navigate(`/customers/${id}/${subPage}`);
  };

  return (
    <div className="switcher" ref={ref}>
      <button
        className="switcher-trigger"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-label="Customer switcher"
      >
        {current ? current.client_name : "Select customer"}
        <span className="caret">▾</span>
      </button>
      {open && (
        <div className="switcher-menu" role="listbox">
          <input
            className="switcher-filter"
            placeholder="Search customers…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            autoFocus
          />
          {clientId && (
            <button className="switcher-item clear" onClick={() => { setOpen(false); navigate("/"); }}>
              Clear selection
            </button>
          )}
          {shown.map((c) => (
            <button
              key={c.client_id}
              className={`switcher-item ${c.client_id === clientId ? "current" : ""}`}
              role="option"
              aria-selected={c.client_id === clientId}
              onClick={() => select(c.client_id)}
            >
              <span>{c.client_name}</span>
              {!c.active && <span className="badge inactive">inactive</span>}
            </button>
          ))}
          {shown.length === 0 && <div className="switcher-empty">No matching customers</div>}
        </div>
      )}
    </div>
  );
}
